import { createAsyncThunk } from "@reduxjs/toolkit"
import { AppId, AppThunkAPIType } from "../../app/appTypes"
import { BlobResponse, deleteWithAuthAndBody, getBlobWithAuth, getWithAuth, postWithAuth, putWithAuth } from "../../http"
import { DocumentSettings } from "../systemSettings/systemDocumentSettings/SystemDocumentSettingsApi"
import { DocSectionType, DocumentLanguages, DocumentTypes } from "./documentTypes"
import { ProjectProcessHeader } from "../project/projectProcess/ProjectProcessApiClient"
import type { CommentPosition } from "./commentsAPI"
import { DateFromModeEnum, DateToModeEnum, IntervalUnitEnum } from "../common/commonTypes"


export type DocumentStatus = 'N' | 'D' | 'I' | 'H' | 'F' | 'C' | "A" | "P" | "E" | "O" | "R" | "X" | "B" | "K" | "W" | "ID"
export type DocumentNumberingMode = "ONLY_SIMPLE_REQ" | "UNIQUE_REQ" | "DECIMAL_HIERARCHICAL"
export type DocumentCreationMode = "Empty" | "Template" | "Document"
export type DocumentKind = 'NORMAL' | 'PROTOCOL' | 'REPORT'
export type DocumentStructure = 'SIMPLE' | 'DYNAMIC' | 'IMPORTED_CUSTOM'
export type DocumentContentOrigin = 'MANUAL' | 'SYSTEM' | 'AI'

export type DocumentDescriptionField = "purpose" | "description" | "area" | "title" | "language"
export type DocumentEditableField = DocumentDescriptionField | "structureId" | "hasAdvancedWFL"
export type DocumentRelationType = "TEMPLATE" | "REPORT" | "BASED_ON" | "REQUIREMENT_PACKAGE"
export type RelationDirectionType = "OUTGOING" | "INCOMING"

export type DocumentContentActions
    = "Read"
    | "Edit"
    | "Comment"
    | "ResolveComment"

    | "DeleteDocument"
    | "CreateTemplate"
    | "CopyDocument"
    | "AddDescriptionField"
    | "PrintReport"
    | "UpdateSettings"

    | "ChangeNumber"
    | "ChangeTitle"

    | "ChangeDeadline"
    | "Changedeadline"
    | "AddTeamMember"
    | "DeleteTeamMember"
    | "SubstituteTeamMember"
    | "SubstituteTM"
    | "InviteTeamMember"
    | "NewDocumentVersion"
    | "CorrectDocument"
    | "CancelDocument"
    | "SysUpdate"
    | "SendReminder"

export const DOCUMENT_STATUSES: DocumentStatus[] = [
    "N",
    "D",
    "I",
    "H",
    "F",
    "C",
    "A",
    "P",
    "E",
    "O",
    "R",
    "X",
    "B",
    "K",
    "W",
    "ID",
]

export const DOCUMENT_CONTENT_ACTIONS: DocumentContentActions[] = [
    "Read",
    "Edit",
    "Comment",
    "ResolveComment",
    "DeleteDocument",
    "CreateTemplate",
    "CopyDocument",
    "AddDescriptionField",
    "PrintReport",
    "UpdateSettings",
    "ChangeNumber",
    "ChangeTitle",
    "ChangeDeadline",
    "Changedeadline",
    "AddTeamMember",
    "DeleteTeamMember",
    "SubstituteTeamMember",
    "SubstituteTM",
    "InviteTeamMember",
    "NewDocumentVersion",
    "CorrectDocument",
    "CancelDocument",
    "SysUpdate",
    "SendReminder",
]

export type DocumentWorkflowActions
    = "SendToOpinion"
    | "ResendToOpinion"
    | "WithdrawFromOpinion"
    | "SendToApprove"
    | "SendToDiscussion"
    | "Approve"
    | "Reject"
    | "FinishOpinion"
    | "ForceFinishOpinion"
    | "ForceFinishApproval"
    | "FinishEdition"
    | "SendToCorrection"
    | "APPROVE"
    | "Finish"
    | "PUBLISH"

export interface DocumentActions {
    documentStatus: string
    workflowActions: DocumentWorkflowActions[]
    documentContentActions: DocumentContentActions[]
}

export type Document = DocumentHeader & {

}

export interface DocumentHeader {
    id: AppId,
    no: string,
    name: string,
    docDesignation: string,
    version: number,
    headDocumentId: number,
    companyId: number,
    companyName: string,
    createDate: string,
    createdBy: string,
    createdByFullName: string,
    guid: string,
    title: string,
    purpose: string,
    area: string,
    description: string,
    readonlyeportTemplate: string,
    language: DocumentLanguages,
    type: string,
    isTemplate: boolean
    isSystem: boolean
    isImported: boolean,
    status: string
    settings: DocumentSeetings[]
    relations: DocumentRelation[]
    structureId: string,
    projectProcessId: string
    isDeleted: boolean,
    relatedCompanyName: string
    documentKind: DocumentKind
    documentStructure: DocumentStructure,
    effectiveFrom: Date,
    effectiveFromMode: DateFromModeEnum,
    effectiveTo: Date,
    effectiveToMode: DateToModeEnum,
    requiresReview: boolean
    reviewIntervalValue: number
    reviewIntervalUnit: IntervalUnitEnum
    reviewDueDate: Date
    lastReviewDate: Date
    isQmsControlled: boolean
    validityIntervalValue?: number
    validityIntervalUnit?: IntervalUnitEnum,
    hasAdvancedWFL: boolean
}

export interface DocumentSection {
    id: AppId
    documentId: number
    code: string
    name: string
    // TODO: description field will be deleted soon
    description: string
    numeration: string
    order: number
    sysSectionId: string
    type: DocSectionType
    modifiedByFullName: string
    modifyDate: string
    parentSectionGuid: string
    subSectionsIds: AppId[]
}

export interface DocumentBlock {
    description: string | null
    sectionId: AppId
}

export interface DocumentSeetings {
    id: AppId,
    name: string,
    value: string
}

export const TESTS_ENABLED_SETTING = "TESTS_ENABLED"

// Whether the RA test column and the reason-level qualification chips are shown.
// Documents created before the setting existed carry no row at all, so a missing
// value means enabled. Written as 'TRUE'/'FALSE', but the older ISHIKAWA setting
// writes 'true'/'false', so read both.
export const isTestsEnabled = (settings: DocumentSeetings[]) => {
    const value = settings.find(setting => setting.name === TESTS_ENABLED_SETTING)?.value
    return value === undefined ? true : value.toLowerCase() === "true"
}

export interface DocumentRelation {
    id: AppId,
    documentId: string,
    relatedDocumentId: string
    relatedDocumentTitle: string
    relatedDocumentNumber: string
    relatedDocumentType: string
    relationType: DocumentRelationType
    direction: RelationDirectionType
}

export interface DocumentRelationsOverview {
    outgoing: DocumentRelation[],
    incoming: DocumentRelation[]
}

interface DocumentFilters {
    companyId: number | "all";
    dateFrom: string;
    dateTo: string;
    allDate: boolean;
    type: DocumentTypes | "all";
    status: DocumentStatus | "all";
}

export const loadAllDocuments = createAsyncThunk<DocumentHeader[], void, AppThunkAPIType>("/documents-overview/load",
    async (_, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({ url: `api/documents-overview` }))
        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as DocumentHeader[]
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })

export const loadAllDocumentsByFilters = createAsyncThunk<DocumentHeader[], DocumentFilters, AppThunkAPIType>("/documents-overview/load",
    async (filters, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({ url: `api/system/documents-overview?allDate=${filters.allDate}&dateFrom=${filters.dateFrom}&dateTo=${filters.dateTo}&type=${filters.type}&status=${filters.status}${filters.companyId !== "all" ? "&companyId=" + filters.companyId : ""}` }))
        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as DocumentHeader[]
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })

export const loadImportedDocumentsByFilters = createAsyncThunk<DocumentHeader[], DocumentTypes, AppThunkAPIType>("/documents-overview/load",
    async (documentType, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({ url: `api/document/imported/${documentType}` }))
        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as DocumentHeader[]
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })

export const loadDocumentsByType = createAsyncThunk<DocumentHeader[], DocumentTypes, AppThunkAPIType>("/documents-overview/load-by-type",
    async (documentType, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({ url: `api/document/by-type/${documentType}` }))
        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as DocumentHeader[]
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })

export const loadDeletedDocuments = createAsyncThunk<DocumentHeader[], void, AppThunkAPIType>("/documents-overview/load-deleted",
    async (_, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({ url: `api/document/deleted` }))
        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as DocumentHeader[]
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })

export const loadDocument = createAsyncThunk<DocumentHeader, string, AppThunkAPIType>(
    "documents/load", async (id, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({
            url: `api/document/${id}/`,
        }))
        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as DocumentHeader
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })

export const loadDocumentVersions = createAsyncThunk<DocumentHeader[], { documentId: AppId; }, AppThunkAPIType>("/documents-versions/load",
    async ({ documentId }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({ url: `api/document/versions/${documentId}` }))
        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as DocumentHeader[]
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })


export const downloadReport = createAsyncThunk<BlobResponse, { documentId: string; language: string }, AppThunkAPIType>(
    "document-report/download",
    async ({ documentId, language }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getBlobWithAuth({ url: `api/reports/document/${documentId}?language=${language}` }));

        if (getBlobWithAuth.fulfilled.match(result)) {
            return result.payload as BlobResponse;
        } else {
            return rejectWithValue(result.payload ?? { kind: 'unknown' });
        }
    }
);

export const downloadReportCommented = createAsyncThunk<BlobResponse, { documentId: string; language: string }, AppThunkAPIType>(
    "document-report/download-commented",
    async ({ documentId, language }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getBlobWithAuth({ url: `api/reports/document-commented/${documentId}?language=${language}` }));

        if (getBlobWithAuth.fulfilled.match(result)) {
            return result.payload as BlobResponse;
        } else {
            return rejectWithValue(result.payload ?? { kind: 'unknown' });
        }
    }
);


export const exportImportedDocumentToExcel = createAsyncThunk<BlobResponse, { documentId: string; language: string }, AppThunkAPIType>(
    "excel-export/imported-document/download",
    async ({ documentId, language }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getBlobWithAuth({ url: `api/excel-export/imported-document/${documentId}?language=${language}` }));

        if (getBlobWithAuth.fulfilled.match(result)) {
            return result.payload as BlobResponse;
        } else {
            return rejectWithValue(result.payload ?? { kind: 'unknown' });
        }
    }
);

export const exportDocumentToExcel = createAsyncThunk<BlobResponse, { documentId: string; language: string }, AppThunkAPIType>(
    "excel-export/document/download",
    async ({ documentId, language }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getBlobWithAuth({ url: `api/excel-export/document/${documentId}?language=${language}` }));

        if (getBlobWithAuth.fulfilled.match(result)) {
            return result.payload as BlobResponse;
        } else {
            return rejectWithValue(result.payload ?? { kind: 'unknown' });
        }
    }
);


export const updateDescriptionField = createAsyncThunk<void, { documentId: AppId, fieldName: DocumentDescriptionField, value: string }, AppThunkAPIType>(
    "document/updateDescriptionField", async (data, { dispatch, getState, rejectWithValue }) => {
        const { documentId, fieldName, value } = data
        const result = await dispatch(putWithAuth({
            url: `api/document/${documentId}/description-fields`,
            payload: { fieldName, text: value },
        }))
        const { payload } = result
        if (putWithAuth.fulfilled.match(result)) {
            return payload
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })

export const updateDocumentField = createAsyncThunk<void, { documentId: AppId, fieldName: DocumentEditableField, value: string }, AppThunkAPIType>(
    "document/updateDocumentField", async (data, { dispatch, getState, rejectWithValue }) => {
        const { documentId, fieldName, value } = data
        const result = await dispatch(putWithAuth({
            url: `api/document/${documentId}/document-fields`,
            payload: { fieldName, text: value },
        }))
        const { payload } = result
        if (putWithAuth.fulfilled.match(result)) {
            return payload
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })

export const updateDocumentNumber = createAsyncThunk<void, { documentId: AppId, documentNumber: string }, AppThunkAPIType>(
    "document/change-number", async (data, { dispatch, getState, rejectWithValue }) => {
        const { documentId, documentNumber } = data
        const result = await dispatch(putWithAuth({
            url: `api/document/${documentId}/change-number`,
            payload: { documentNumber: documentNumber },
        }))
        const { payload } = result
        if (putWithAuth.fulfilled.match(result)) {
            return payload
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })


export const updateCounterparty = createAsyncThunk<void, { documentId: AppId, counterpartyId: string | null }, AppThunkAPIType>(
    "document/change-counterparty", async (data, { dispatch, getState, rejectWithValue }) => {
        const { documentId, counterpartyId } = data
        const result = await dispatch(putWithAuth({
            url: `api/document/${documentId}/change-counterparty`,
            payload: { counterpartyId: counterpartyId },
        }))
        const { payload } = result
        if (putWithAuth.fulfilled.match(result)) {
            return payload
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })

export const updateDocumentSetting = createAsyncThunk<DocumentSettings, { documentId: AppId, name: string, value: string }, AppThunkAPIType>(
    "document/updateDocumentSetting", async (data, { dispatch, rejectWithValue }) => {
        const { documentId, name, value } = data
        const result = await dispatch(putWithAuth({
            url: `api/document/${documentId}/settings`,
            payload: { name: name, value: value },
        }))
        const { payload } = result
        if (putWithAuth.fulfilled.match(result)) {
            return {
                name,
                value,
                id: '',
            } as DocumentSettings
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })

export const loadDocumentActions = createAsyncThunk<DocumentActions, string, AppThunkAPIType>(
    "documents/load-actions", async (documentId, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({
            url: `api/document-actions/${documentId}/`,
        }))
        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as DocumentActions
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })


export interface PerformActionPayload {
    action: DocumentWorkflowActions;
    password: string;
    deadline?: string;
    comment?: string;
    forceExecution?: boolean;
}

export type DocumentActionValidationResponse = {
    errors: string[]
    warnings: string[]
}

export const validateDocumentAction = createAsyncThunk<
    DocumentActionValidationResponse,
    { documentId: AppId; action: DocumentWorkflowActions },
    AppThunkAPIType
>(
    "document/validateDocumentAction",
    async ({ documentId, action }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(
            postWithAuth({
                url: `api/document-actions/${documentId}/validate-action`,
                payload: { action },
            })
        )

        if (postWithAuth.fulfilled.match(result)) {
            return result.payload as DocumentActionValidationResponse
        }

        return rejectWithValue(result.payload ?? { kind: "unknown" })
    }
)

export interface DocumentActionResponse {
    documentStatus: string
    workflowActions: DocumentWorkflowActions[]
    documentContentActions: DocumentContentActions[]
    documentHeader: DocumentHeader
}

export const performDocumentAction = createAsyncThunk<DocumentActionResponse, { documentId: AppId, actionPayload: PerformActionPayload }, AppThunkAPIType>(
    "document/performdocumentaction", async (data, { dispatch, rejectWithValue }) => {
        const { documentId, actionPayload } = data
        const result = await dispatch(postWithAuth({
            url: `api/document-actions/${documentId}/runaction`,
            payload: actionPayload,
        }))
        const { payload } = result
        if (postWithAuth.fulfilled.match(result)) {
            return payload as DocumentActionResponse
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })

export const resetWorkflow = createAsyncThunk<DocumentHeader, string, AppThunkAPIType>(
    "document/reset-workflow", async (documentId, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({
            url: `api/system/documents-overview/${documentId}/reset-workflow`, payload: {}
        }))
        const { payload } = result
        if (postWithAuth.fulfilled.match(result)) {
            return payload as DocumentHeader
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })


export const softDeleteDocument = createAsyncThunk<boolean, { documentId: string; password: string }, AppThunkAPIType>(
    "document/soft-delete", async ({ documentId, password }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({
            url: `api/document/${documentId}/soft-delete`,
            payload: { password }
        }))
        const { payload } = result
        if (postWithAuth.fulfilled.match(result)) {
            return true
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })

export const restoreDeletedDocument = createAsyncThunk<boolean, { documentId: string; password: string }, AppThunkAPIType>(
    "document/restore-deleted", async ({ documentId, password }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({
            url: `api/document/${documentId}/restore-deleted`,
            payload: { password }
        }))
        const { payload } = result
        if (postWithAuth.fulfilled.match(result)) {
            return true
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })


export const permanentDeleteDocument = createAsyncThunk<boolean, { documentId: string; password: string }, AppThunkAPIType>(
    "document/permanent-delete", async ({ documentId, password }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({
            url: `api/document/${documentId}/permanent-delete`,
            payload: { password }
        }))
        const { payload } = result
        if (postWithAuth.fulfilled.match(result)) {
            return true
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })

export interface DocExistsResponse {
    exists: boolean
}

export interface DocExistsInput {
    documentNumber: string
    documentType: DocumentTypes
}

export interface ValidateDocumentNumberResponse {
    isValid: boolean
}

export interface ValidateDocumentNumberInput {
    documentNumber: string
    documentType: DocumentTypes
}

export const validateDocumentNumber = createAsyncThunk<ValidateDocumentNumberResponse, ValidateDocumentNumberInput, AppThunkAPIType>(
    "document/validate-number", async (input, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({
            url: "api/document/validate-number",
            payload: input,
        }))

        const { payload } = result
        if (postWithAuth.fulfilled.match(result)) {
            return payload as ValidateDocumentNumberResponse
        }

        return rejectWithValue(payload ?? { kind: 'unknown' })
    }
)

export const documentExists = createAsyncThunk<DocExistsResponse, DocExistsInput, AppThunkAPIType>(
    "document/exists", async (input, { dispatch, rejectWithValue }) => {
        const rawUrl = `api/document/exists?documentNumber=${input.documentNumber}&documentType=${input.documentType}`
        const result = await dispatch(getWithAuth({
            url: encodeURI(rawUrl),
        }))
        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as DocExistsResponse
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const loadDocumentSections = createAsyncThunk<DocumentSection[], string, AppThunkAPIType>(
    "documents/load-sections", async (documentId, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({
            url: `api/document-sections/${documentId}/`,
        }))
        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as DocumentSection[]
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })


export const createDocSection = createAsyncThunk<DocumentSection, {
    documentId: AppId
    code: string
    name: string
    description: string
    type: DocSectionType
    parentSectionId: string | null
}, AppThunkAPIType>('documents/create-section', async (arg, api) => {
    const { dispatch, rejectWithValue } = api
    const result = await dispatch(postWithAuth({
        url: `api/document-sections/${arg.documentId}/`,
        payload: arg
    }))
    const { payload } = result
    if (postWithAuth.fulfilled.match(result)) {
        return payload as DocumentSection
    } else {
        return rejectWithValue(payload ?? { kind: 'unknown' })
    }
})

export const updateDocSection = createAsyncThunk<DocumentSection, DocumentSection, AppThunkAPIType>(
    "document/update-section", async (data, { dispatch, rejectWithValue }) => {
        const { documentId, id: sectionId } = data
        const result = await dispatch(putWithAuth({
            url: `api/document-sections/${documentId}/${sectionId}`,
            payload: data,
        }))
        const { payload } = result
        if (putWithAuth.fulfilled.match(result)) {
            return payload as DocumentSection
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })

export const deleteDocSection = createAsyncThunk<
    { sections: DocumentSection[]; id: number },
    { documentId: number; sectionId: AppId },
    AppThunkAPIType
>(
    "document/delete-section",
    async ({ documentId, sectionId }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(
            deleteWithAuthAndBody({
                url: `api/document-sections/${documentId}/${sectionId}`,
            })
        )

        const { payload } = result

        if (deleteWithAuthAndBody.fulfilled.match(result)) {
            return {
                id: documentId,
                sections: payload as DocumentSection[],
            }
        }

        return rejectWithValue(payload ?? { kind: "unknown" })
    }
)
export const moveDocSection = createAsyncThunk<DocumentSection[], {
    documentId: AppId
    sectionId: AppId
    newParentId: string | null
    insertBeforeSectionId: string | null
}, AppThunkAPIType>('documents/move-section', async (arg, api) => {
    const { dispatch, rejectWithValue } = api
    const { documentId, ...data } = arg
    const result = await dispatch(postWithAuth({
        url: `api/document-sections/${documentId}/move`,
        payload: data,
    }))
    const { payload } = result
    if (postWithAuth.fulfilled.match(result)) {
        return payload as DocumentSection[]
    } else {
        return rejectWithValue(payload ?? { kind: 'unknown' })
    }
})

export const loadDocBlock = createAsyncThunk<DocumentBlock, { docId: AppId, sectionId: string }, AppThunkAPIType>(
    "documents/load-sections-content", async ({ docId, sectionId }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({
            url: `api/document-sections/${docId}/content/${sectionId}`,
        }))
        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as DocumentBlock
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })

// positions: comment anchors that moved with the content, saved in the same transaction
export const updateDocBlock = createAsyncThunk<DocumentBlock, DocumentBlock & { docId: AppId, positions?: CommentPosition[] }, AppThunkAPIType>(
    "document/update-section-content", async (data, { dispatch, rejectWithValue }) => {
        const { docId, sectionId } = data
        const result = await dispatch(putWithAuth({
            url: `api/document-sections/${docId}/content/${sectionId}`,
            payload: data,
        }))
        const { payload } = result
        if (putWithAuth.fulfilled.match(result)) {
            return payload as DocumentBlock
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })

export const generateDocBlock = createAsyncThunk<DocumentBlock, DocumentBlock & { docId: AppId }, AppThunkAPIType>(
    "document/generate-section-content", async (data, { dispatch, rejectWithValue }) => {
        const { docId, sectionId } = data
        const result = await dispatch(postWithAuth({
            url: `api/document-sections/${docId}/content/${sectionId}/generate`,
            payload: data,
        }))
        const { payload } = result
        if (postWithAuth.fulfilled.match(result)) {
            return payload as DocumentBlock
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })


export interface DocumentRejectedAction {
    rejectedAt: string | null
    rejectedBy: string
    rejectedByFullName: string
    rejectionReason: string
}

export interface DocumentRejectionInfo extends DocumentRejectedAction {
    documentId: number
    rejectedActions: DocumentRejectedAction[]
}

export const loadDocumentRejectionInfo = createAsyncThunk<DocumentRejectionInfo, string, AppThunkAPIType>(
    "documents/loadDocumentRejectionInfo", async (id, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({
            url: `api/document/rejection-info/${id}/`,
        }))
        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as DocumentRejectionInfo
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })

export interface CreateDocumentNewVersion {
    documentId: AppId,
    password: string,
    changeReason: string,
    changeDescription: string,
}

export const createDocumentNewVersion = createAsyncThunk<AppId, CreateDocumentNewVersion, AppThunkAPIType>(
    "document/createDocumentNewVersion", async (data, { dispatch, rejectWithValue }) => {
        const { documentId, password, changeReason, changeDescription } = data
        const result = await dispatch(postWithAuth({
            url: `api/document/${documentId}/new-version`,
            payload: {
                documentId,
                password,
                changeReason,
                changeDescription
            },
        }))
        const { payload } = result
        if (postWithAuth.fulfilled.match(result)) {
            return payload as AppId
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })


export const loadDocumentRelationsOverview = createAsyncThunk<DocumentRelationsOverview, string, AppThunkAPIType>(
    "documents/relationsOverview", async (id, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({
            url: `api/document-relations/${id}/overview`,
        }))
        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as DocumentRelationsOverview
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })


export const loadAllDocumentProcesses = createAsyncThunk<ProjectProcessHeader[], string, AppThunkAPIType>(
    "documents/allprocesses", async (id, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({
            url: `api/document-processes/${id}/all`,
        }))
        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as ProjectProcessHeader[]
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })

export interface UpdateDocument {
    documentId: AppId,
    password: string,
    date?: string,
    status?: string,
    author?: string,
    opinionDate?: Date | string,
    approvementDate?: Date | string,
    changeLogs?: {
        id: string;
        createDate: Date | string;
    }[],
}

export const updateDocument = createAsyncThunk<void, UpdateDocument, AppThunkAPIType>(
    "document/updateDocument", async (data, { dispatch, rejectWithValue }) => {
        const { documentId, ...requestPayload } = data
        const result = await dispatch(postWithAuth({
            url: `api/document/${documentId}/sys-update`,
            payload: {
                documentId,
                ...requestPayload,
            },
        }))
        const { payload } = result
        if (postWithAuth.fulfilled.match(result)) {
            return
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })
